import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  Plus, Settings, Upload, Save, Trash2, X, LineChart as LineChartIcon,
  Table as TableIcon, History, Calendar, Loader2, ClipboardPaste, Building2, Download,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// A single-file matrix editor for the competitor ad-count tracker.
// Dates run across the top; metrics (Total, per-product, per-ad-type) run down
// the side. Cells are inline-editable; Enter commits.

const fmt = (d) => { try { return format(parseISO(d), 'dd/MM/yyyy'); } catch { return d; } };

const kindColor = {
  total: 'text-slate-100 font-semibold',
  product: 'text-cyan-200',
  ad_type: 'text-fuchsia-200',
};

const kindPill = {
  total: 'bg-slate-500/20 text-slate-100 border border-slate-400/30',
  product: 'bg-cyan-500/20 text-cyan-100 border border-cyan-400/30',
  ad_type: 'bg-fuchsia-500/20 text-fuchsia-100 border border-fuchsia-400/30',
};

export default function CompetitorAdCountTracker() {
  const [brands, setBrands] = useState([]);
  const [selectedBrandId, setSelectedBrandId] = useState('');
  const [matrix, setMatrix] = useState(null);
  const [loadingMatrix, setLoadingMatrix] = useState(false);
  const [view, setView] = useState('matrix'); // matrix | chart | audit
  const [audit, setAudit] = useState([]);
  const [showBrandModal, setShowBrandModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showAddDateModal, setShowAddDateModal] = useState(false);
  const [chartRowKey, setChartRowKey] = useState(''); // "kind::name"

  useEffect(() => {
    fetchBrands();
  }, []);

  useEffect(() => {
    if (selectedBrandId) {
      fetchMatrix(selectedBrandId);
      if (view === 'audit') fetchAudit(selectedBrandId);
    }
  }, [selectedBrandId, view]);

  const fetchBrands = async () => {
    try {
      const r = await fetch(`${API_URL}/api/competitor-tracker/brands`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setBrands(data);
      if (!selectedBrandId && data.length > 0) setSelectedBrandId(data[0].brand_id);
    } catch (e) {
      toast.error(`Failed to load brands: ${e.message}`);
    }
  };

  const fetchMatrix = async (bid) => {
    setLoadingMatrix(true);
    try {
      const r = await fetch(`${API_URL}/api/competitor-tracker/matrix/${bid}`, { credentials: 'include' });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      setMatrix(data);
      if (!chartRowKey && data.rows[0]) setChartRowKey(`${data.rows[0].kind}::${data.rows[0].name}`);
    } catch (e) {
      toast.error(`Failed to load matrix: ${e.message}`);
    } finally { setLoadingMatrix(false); }
  };

  const fetchAudit = async (bid) => {
    try {
      const r = await fetch(`${API_URL}/api/competitor-tracker/audit/${bid}?limit=100`, { credentials: 'include' });
      const data = await r.json();
      if (r.ok) setAudit(data);
    } catch { /* soft-fail */ }
  };

  const exportMatrix = () => {
    if (!matrix || matrix.dates.length === 0) return;
    // Emit the same matrix shape the UI shows so a round-trip export → edit →
    // paste back through Bulk Import works. CSV cell escaping wraps values
    // containing "," or quote in double-quotes.
    const esc = (v) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    // dd/mm/yyyy on the date row — matches the Indian convention the team uses.
    const headers = ['Date', ...matrix.dates.map(d => {
      const [y, m, day] = d.split('-');
      return `${day}/${m}/${y}`;
    })];
    const lines = [headers.map(esc).join(',')];
    for (const row of matrix.rows) {
      const cells = [row.name, ...matrix.dates.map(d => row.values?.[d] ?? '')];
      lines.push(cells.map(esc).join(','));
    }
    const content = lines.join('\n');
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${matrix.brand.brand_name.replace(/\s+/g, '_')}_ad_counts_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Exported CSV');
  };


  const upsertCell = async (date, kind, name, count) => {
    try {
      const r = await fetch(`${API_URL}/api/competitor-tracker/entries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ brand_id: selectedBrandId, date, kind, name, count }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      // Optimistic patch of local matrix — no need to re-fetch.
      setMatrix(m => {
        if (!m) return m;
        const rows = m.rows.map(row => {
          if (row.kind !== kind || row.name !== name) return row;
          const values = { ...row.values };
          if (count === null || count === '' || count === undefined) delete values[date];
          else values[date] = Number(count);
          return { ...row, values };
        });
        return { ...m, rows };
      });
      return true;
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
      return false;
    }
  };

  return (
    <div className="space-y-4" data-testid="competitor-ad-count-tracker">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex-1 min-w-[240px]">
          <Select value={selectedBrandId} onValueChange={setSelectedBrandId}>
            <SelectTrigger className="bg-slate-900/70 border-slate-700 text-slate-100" data-testid="tracker-brand-select">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-slate-400" />
                <SelectValue placeholder={brands.length ? 'Pick a brand' : 'No brands yet — add one'} />
              </div>
            </SelectTrigger>
            <SelectContent>
              {brands.map(b => (<SelectItem key={b.brand_id} value={b.brand_id}>{b.brand_name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <Button
          variant="outline"
          onClick={() => setShowBrandModal(true)}
          data-testid="tracker-brands-btn"
          className="bg-slate-800 border-slate-600 text-slate-100 hover:bg-slate-700 hover:text-white"
        >
          <Settings className="h-4 w-4 mr-1" /> Manage Brands
        </Button>
        <Button
          onClick={() => setShowAddDateModal(true)}
          disabled={!selectedBrandId}
          data-testid="tracker-add-date-btn"
        >
          <Plus className="h-4 w-4 mr-1" /> Add Entry
        </Button>
        <Button
          variant="outline"
          onClick={() => setShowBulkModal(true)}
          disabled={!selectedBrandId}
          data-testid="tracker-bulk-btn"
          className="bg-slate-800 border-slate-600 text-slate-100 hover:bg-slate-700 hover:text-white disabled:opacity-50"
        >
          <Upload className="h-4 w-4 mr-1" /> Bulk Import
        </Button>
        <Button
          variant="outline"
          onClick={exportMatrix}
          disabled={!matrix || matrix.dates.length === 0}
          data-testid="tracker-export-btn"
          className="bg-slate-800 border-slate-600 text-slate-100 hover:bg-slate-700 hover:text-white disabled:opacity-50"
          title={!matrix || matrix.dates.length === 0 ? 'No data to export yet' : 'Download current matrix as CSV'}
        >
          <Download className="h-4 w-4 mr-1" /> Export
        </Button>
      </div>

      <div className="flex gap-1 bg-slate-900/50 p-1 rounded-lg w-fit">
        {[
          { k: 'matrix', l: 'Matrix', I: TableIcon },
          { k: 'chart', l: 'Trend', I: LineChartIcon },
          { k: 'audit', l: 'History', I: History },
        ].map(t => (
          <button key={t.k} onClick={() => setView(t.k)} data-testid={`tracker-tab-${t.k}`}
            className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1 transition
              ${view === t.k ? 'bg-cyan-500 text-slate-950' : 'text-slate-300 hover:text-white'}`}>
            <t.I className="h-3.5 w-3.5" /> {t.l}
          </button>
        ))}
      </div>

      {loadingMatrix && (
        <div className="flex items-center justify-center py-12 text-slate-400 gap-2">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading…
        </div>
      )}

      {!loadingMatrix && matrix && view === 'matrix' && (
        <MatrixTable matrix={matrix} onCellSave={upsertCell} />
      )}

      {!loadingMatrix && matrix && view === 'chart' && (
        <ChartView matrix={matrix} rowKey={chartRowKey} setRowKey={setChartRowKey} />
      )}

      {!loadingMatrix && matrix && view === 'audit' && (
        <AuditView audit={audit} />
      )}

      {showBrandModal && (
        <BrandModal
          brands={brands}
          onClose={() => setShowBrandModal(false)}
          onSaved={() => { fetchBrands(); if (selectedBrandId) fetchMatrix(selectedBrandId); }}
        />
      )}

      {showBulkModal && matrix && (
        <BulkImportModal
          brand={matrix.brand}
          onClose={() => setShowBulkModal(false)}
          onImported={() => { fetchMatrix(selectedBrandId); toast.success('Bulk import complete'); }}
        />
      )}

      {showAddDateModal && matrix && (
        <AddDateModal
          matrix={matrix}
          onClose={() => setShowAddDateModal(false)}
          onSaved={() => fetchMatrix(selectedBrandId)}
        />
      )}
    </div>
  );
}

// ============ Matrix table with inline-edit cells ============

function MatrixTable({ matrix, onCellSave }) {
  if (!matrix.dates.length) {
    return (
      <Card className="bg-slate-900/50 border-slate-700 p-8 text-center" data-testid="tracker-empty">
        <Calendar className="h-8 w-8 mx-auto text-slate-500 mb-2" />
        <p className="text-slate-300 mb-1">No data yet for <span className="font-semibold">{matrix.brand.brand_name}</span></p>
        <p className="text-sm text-slate-500">Click "Add Entry" or "Bulk Import" to start tracking.</p>
      </Card>
    );
  }
  return (
    <Card className="bg-slate-900/60 border-slate-700 overflow-auto max-h-[70vh]" data-testid="tracker-matrix-table">
      <table className="w-full text-sm">
        <thead className="bg-slate-950/70 sticky top-0 z-10">
          <tr>
            <th className="text-left px-3 py-2 text-slate-400 font-medium border-b border-slate-700 sticky left-0 bg-slate-950/90 z-20 min-w-[180px]">Metric</th>
            {matrix.dates.map(d => (
              <th key={d} className="px-3 py-2 text-slate-400 font-medium border-b border-slate-700 min-w-[100px] text-right whitespace-nowrap">{fmt(d)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrix.rows.map((row) => (
            <tr key={`${row.kind}::${row.name}`} className={`border-b border-slate-800/70 hover:bg-slate-800/30 ${row.kind === 'total' ? 'bg-slate-800/40' : ''}`}>
              <td className="px-3 py-2 sticky left-0 bg-slate-900/95 z-10">
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${kindPill[row.kind]}`}>{row.kind === 'ad_type' ? 'TYPE' : row.kind.toUpperCase()}</span>
                  <span className={kindColor[row.kind]}>{row.name}</span>
                  {row.orphaned && (<span className="text-[10px] px-1 py-0.5 rounded bg-amber-500/20 text-amber-200 border border-amber-500/30">orphan</span>)}
                </div>
              </td>
              {matrix.dates.map(d => (
                <MatrixCell
                  key={d}
                  row={row}
                  date={d}
                  value={row.values?.[d]}
                  onSave={(newVal) => onCellSave(d, row.kind, row.name, newVal)}
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function MatrixCell({ row, date, value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value ?? ''); }, [value]);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const commit = async () => {
    const trimmed = String(draft).trim();
    const newVal = trimmed === '' ? null : Number(trimmed);
    if (newVal !== null && (Number.isNaN(newVal) || newVal < 0)) {
      toast.error('Enter a non-negative number');
      setDraft(value ?? '');
      setEditing(false);
      return;
    }
    if ((value ?? null) === newVal) { setEditing(false); return; }
    const ok = await onSave(newVal);
    if (!ok) setDraft(value ?? '');
    setEditing(false);
  };

  return (
    <td
      onClick={() => !editing && setEditing(true)}
      onKeyDown={(e) => { if (!editing && e.key === 'Enter') setEditing(true); }}
      className="px-2 py-1 text-right cursor-text hover:bg-cyan-500/10 min-w-[100px]"
      data-testid={`tracker-cell-${row.kind}-${row.name}-${date}`}
      tabIndex={0}
    >
      {editing ? (
        <input
          ref={inputRef}
          type="number"
          value={draft}
          min={0}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            else if (e.key === 'Escape') { setDraft(value ?? ''); setEditing(false); }
          }}
          className="w-full bg-slate-950 border border-cyan-500 rounded px-2 py-0.5 text-right text-slate-100 outline-none"
        />
      ) : (
        <span className={value === undefined ? 'text-slate-600' : 'text-slate-100'}>
          {value === undefined ? '—' : value.toLocaleString()}
        </span>
      )}
    </td>
  );
}

// ============ Chart view (line chart of one metric over time) ============
function ChartView({ matrix, rowKey, setRowKey }) {
  const row = matrix.rows.find(r => `${r.kind}::${r.name}` === rowKey);
  const data = row ? matrix.dates.map(d => ({ date: fmt(d), value: row.values?.[d] ?? null })) : [];
  return (
    <Card className="bg-slate-900/60 border-slate-700 p-4" data-testid="tracker-chart-view">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-sm text-slate-400">Metric:</span>
        <Select value={rowKey} onValueChange={setRowKey}>
          <SelectTrigger className="w-72 bg-slate-950 border-slate-700 text-slate-100" data-testid="tracker-chart-metric">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {matrix.rows.map(r => (
              <SelectItem key={`${r.kind}::${r.name}`} value={`${r.kind}::${r.name}`}>
                <span className="text-[10px] px-1 py-0.5 rounded mr-2 bg-slate-800 text-slate-300">{r.kind === 'ad_type' ? 'TYPE' : r.kind.toUpperCase()}</span>
                {r.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {data.length === 0 ? (
        <div className="text-center text-slate-500 py-12">No data.</div>
      ) : (
        <div style={{ width: '100%', height: 340 }}>
          <ResponsiveContainer>
            <LineChart data={data} margin={{ top: 12, right: 20, left: 0, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="date" stroke="#94a3b8" tick={{ fontSize: 11 }} />
              <YAxis stroke="#94a3b8" tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', color: '#e2e8f0' }} />
              <Legend />
              <Line type="monotone" dataKey="value" name={row?.name || '—'} stroke="#22d3ee" strokeWidth={2} dot={{ r: 3 }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

// ============ Audit view ============
function AuditView({ audit }) {
  if (!audit.length) return <div className="text-center text-slate-500 py-12" data-testid="tracker-audit-empty">No audit history yet.</div>;
  return (
    <Card className="bg-slate-900/60 border-slate-700 overflow-auto max-h-[70vh]" data-testid="tracker-audit-view">
      <table className="w-full text-sm">
        <thead className="bg-slate-950/70 sticky top-0">
          <tr>
            {['When', 'Who', 'Action', 'Metric', 'Date', 'Old → New'].map(h =>
              <th key={h} className="text-left px-3 py-2 text-slate-400 font-medium border-b border-slate-700">{h}</th>
            )}
          </tr>
        </thead>
        <tbody>
          {audit.map((a) => (
            <tr key={a.id} className="border-b border-slate-800/70 hover:bg-slate-800/30">
              <td className="px-3 py-1.5 text-slate-300 whitespace-nowrap">{a.at ? format(parseISO(a.at), 'dd MMM, HH:mm') : '—'}</td>
              <td className="px-3 py-1.5 text-slate-300">{a.actor}</td>
              <td className="px-3 py-1.5">
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${a.action === 'delete' ? 'bg-red-500/20 text-red-200 border border-red-500/30' : a.action === 'update' ? 'bg-amber-500/20 text-amber-200 border border-amber-500/30' : 'bg-emerald-500/20 text-emerald-200 border border-emerald-500/30'}`}>
                  {a.action}
                </span>
              </td>
              <td className="px-3 py-1.5 text-slate-100">
                <span className="text-[10px] mr-1 px-1 py-0.5 rounded bg-slate-800 text-slate-400">{a.kind === 'ad_type' ? 'TYPE' : a.kind.toUpperCase()}</span>{a.name}
              </td>
              <td className="px-3 py-1.5 text-slate-300 whitespace-nowrap">{a.date ? fmt(a.date) : '—'}</td>
              <td className="px-3 py-1.5 text-slate-300 whitespace-nowrap">
                <span className="text-slate-500">{a.old_count ?? '—'}</span>
                <span className="mx-1 text-slate-500">→</span>
                <span className="text-slate-100">{a.new_count ?? '—'}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

// ============ Brand management modal ============
function BrandModal({ brands, onClose, onSaved }) {
  const [editingId, setEditingId] = useState(null);
  const [brandName, setBrandName] = useState('');
  const [productsText, setProductsText] = useState('');
  const [adTypesText, setAdTypesText] = useState('Static, Carousel, Video');
  const [saving, setSaving] = useState(false);

  const loadEdit = (b) => {
    setEditingId(b.brand_id);
    setBrandName(b.brand_name);
    setProductsText((b.products || []).join(', '));
    setAdTypesText((b.ad_types || []).join(', '));
  };
  const clear = () => { setEditingId(null); setBrandName(''); setProductsText(''); setAdTypesText('Static, Carousel, Video'); };

  const save = async () => {
    if (!brandName.trim()) { toast.error('Brand name required'); return; }
    setSaving(true);
    try {
      const body = {
        brand_id: editingId || undefined,
        brand_name: brandName.trim(),
        products: productsText.split(/[,\n]/).map(s => s.trim()).filter(Boolean),
        ad_types: adTypesText.split(/[,\n]/).map(s => s.trim()).filter(Boolean),
      };
      const r = await fetch(`${API_URL}/api/competitor-tracker/brands`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      toast.success(editingId ? 'Brand updated' : 'Brand added');
      clear();
      onSaved();
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    } finally { setSaving(false); }
  };

  const del = async (b) => {
    if (!window.confirm(`Delete "${b.brand_name}" and ALL its tracked data? This cannot be undone.`)) return;
    try {
      const r = await fetch(`${API_URL}/api/competitor-tracker/brands/${b.brand_id}`, { method: 'DELETE', credentials: 'include' });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      toast.success('Brand deleted');
      onSaved();
    } catch (e) {
      toast.error(`Delete failed: ${e.message}`);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-2xl">
        <DialogHeader>
          <DialogTitle>Manage Brands, Products & Ad Types</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
          <div className="space-y-3">
            <h4 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Existing brands</h4>
            <div className="max-h-[300px] overflow-auto space-y-2">
              {brands.length === 0 && <p className="text-sm text-slate-500">None yet.</p>}
              {brands.map(b => (
                <div key={b.brand_id} className="flex items-center justify-between bg-slate-800/50 rounded p-2 gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-slate-100 font-medium truncate" data-testid={`tracker-brand-item-${b.brand_id}`}>{b.brand_name}</div>
                    <div className="text-xs text-slate-500 truncate">{(b.products || []).length} products · {(b.ad_types || []).length} types</div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => loadEdit(b)} className="h-7 px-2">Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => del(b)} className="h-7 px-2 text-red-300 hover:text-red-100"><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-3">
            <h4 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">{editingId ? 'Edit brand' : 'Add new brand'}</h4>
            <Input placeholder="Brand name (e.g. MuscleBlaze)" value={brandName} onChange={e => setBrandName(e.target.value)} data-testid="tracker-brand-name-input"
              className="bg-slate-800 border-slate-700" />
            <div>
              <label className="text-xs text-slate-400">Products (comma or newline separated)</label>
              <Textarea rows={3} placeholder="Creatine, Preworkout, Fish Oil" value={productsText} onChange={e => setProductsText(e.target.value)}
                data-testid="tracker-brand-products-input" className="bg-slate-800 border-slate-700 mt-1" />
            </div>
            <div>
              <label className="text-xs text-slate-400">Ad types (comma or newline separated)</label>
              <Textarea rows={2} placeholder="Static, Carousel, Video" value={adTypesText} onChange={e => setAdTypesText(e.target.value)}
                data-testid="tracker-brand-adtypes-input" className="bg-slate-800 border-slate-700 mt-1" />
            </div>
            <div className="flex gap-2">
              <Button onClick={save} disabled={saving} data-testid="tracker-brand-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} {editingId ? 'Update' : 'Add'}
              </Button>
              {editingId && <Button variant="outline" onClick={clear}><X className="h-4 w-4 mr-1" /> Cancel edit</Button>}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============ Add-Date modal ============
function AddDateModal({ matrix, onClose, onSaved }) {
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [values, setValues] = useState({}); // "kind::name" -> string
  const [saving, setSaving] = useState(false);

  const rows = useMemo(() => matrix.rows.filter(r => !r.orphaned), [matrix]);

  const save = async () => {
    setSaving(true);
    try {
      const promises = rows.map(async (r) => {
        const key = `${r.kind}::${r.name}`;
        const v = (values[key] ?? '').toString().trim();
        if (!v) return null;
        const n = Number(v);
        if (Number.isNaN(n) || n < 0) return null;
        return fetch(`${API_URL}/api/competitor-tracker/entries`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ brand_id: matrix.brand.brand_id, date, kind: r.kind, name: r.name, count: n }),
        });
      });
      await Promise.all(promises);
      toast.success('Entries saved');
      onSaved(); onClose();
    } catch (e) { toast.error(`Save failed: ${e.message}`); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-lg">
        <DialogHeader><DialogTitle>Add data for a date · {matrix.brand.brand_name}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="text-xs text-slate-400">Date</label>
            <Input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="tracker-add-date-input" className="bg-slate-800 border-slate-700 mt-1" />
          </div>
          <div className="max-h-[50vh] overflow-auto space-y-2 pr-1">
            {rows.map(r => {
              const key = `${r.kind}::${r.name}`;
              return (
                <div key={key} className="flex items-center gap-2">
                  <span className={`text-[10px] px-1 py-0.5 rounded ${kindPill[r.kind]}`}>{r.kind === 'ad_type' ? 'TYPE' : r.kind.toUpperCase()}</span>
                  <span className="flex-1 text-slate-200 text-sm">{r.name}</span>
                  <Input
                    type="number"
                    min={0}
                    placeholder="—"
                    value={values[key] ?? ''}
                    onChange={e => setValues(v => ({ ...v, [key]: e.target.value }))}
                    className="w-24 bg-slate-800 border-slate-700 text-right"
                    data-testid={`tracker-add-input-${r.kind}-${r.name}`}
                  />
                </div>
              );
            })}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="tracker-add-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============ Bulk import modal (paste or CSV upload) ============
function BulkImportModal({ brand, onClose, onImported }) {
  const [text, setText] = useState('');
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // Cap at ~10MB — the matrix is text, so anything larger is likely a mistake.
    if (f.size > 10 * 1024 * 1024) {
      toast.error('File too large (max 10MB)');
      e.target.value = '';
      return;
    }
    try {
      const content = await f.text();
      setText(content);
      toast.success(`Loaded ${f.name} · click Import to save`);
    } catch (err) {
      toast.error(`Could not read file: ${err.message}`);
    } finally {
      // Reset so re-picking the same file re-fires onChange.
      e.target.value = '';
    }
  };

  const downloadTemplate = () => {
    // Multi-brand matrix template. First column = Brand, second = Metric, then dates.
    // Empty cells mean "no data" (not zero). The Bulk Import parser auto-detects
    // the Brand column and creates brands that don't yet exist.
    const dates = ['06/07/2026', '09/07/2026', '15/07/2026', '20/07/2026'];
    const header = ['Brand', 'Metric', ...dates].join(',');
    const rows = [
      ['MuscleBlaze', 'Total Active Ads', 1300, 950, 800, 650].join(','),
      ['MuscleBlaze', 'Creatine', 550, 460, 410, 290].join(','),
      ['MuscleBlaze', 'Preworkout', '', 18, 11, 11].join(','),
      ['MuscleBlaze', 'Fish Oil', '', 25, 21, 19].join(','),
      ['MuscleBlaze', 'Video', '', '', '', 420].join(','),
      ['GNC', 'Total Active Ads', 900, 850, 800, 700].join(','),
      ['GNC', 'Creatine', 320, 300, 280, 260].join(','),
    ];
    const content = [header, ...rows].join('\n');
    const filename = `tracker_multibrand_template.csv`;
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const fillSample = () => {
    // A short two-brand sample. Same shape as the downloaded template so users
    // learn by example. Uses tabs so pasting looks clean in the textarea.
    const dates = ['06/07/2026', '09/07/2026', '15/07/2026', '20/07/2026'];
    const lines = [
      ['Brand', 'Metric', ...dates].join('\t'),
      ['MuscleBlaze', 'Total Active Ads', 1300, 950, 800, 650].join('\t'),
      ['MuscleBlaze', 'Creatine', 550, 460, 410, 290].join('\t'),
      ['MuscleBlaze', 'Preworkout', '', 18, 11, 11].join('\t'),
      ['MuscleBlaze', 'Video', '', '', '', 420].join('\t'),
      ['GNC', 'Total Active Ads', 900, 850, 800, 700].join('\t'),
      ['GNC', 'Creatine', 320, 300, 280, 260].join('\t'),
    ];
    setText(lines.join('\n'));
  };

  const parsePasted = () => {
    // Supports two multi-brand layouts:
    //  a) MATRIX with an optional Brand column:
    //     [Brand, Metric, d1, d2, ...] header  →  each row [brandName, metricName, v1, v2, ...]
    //     If no "Brand" header cell, we fall back to the currently-selected brand.
    //  b) LONG with an optional brand column:
    //     [date, brand, kind, name, count] (kind and brand both optional)
    const lines = text.split(/\r?\n/).map(l => l.trimEnd()).filter(l => l.trim());
    if (lines.length < 2) return null;
    const splitter = (l) => l.includes('\t') ? l.split('\t') : l.split(/,(?=(?:[^"]*"[^"]*")*[^"]*$)/).map(s => s.replace(/^"|"$/g, ''));

    const first = splitter(lines[0]).map(s => s.trim());
    const lowered = first.map(s => s.toLowerCase());
    const hasCount = lowered.includes('count') || lowered.includes('value');
    const hasName = lowered.includes('name') || lowered.includes('metric');
    const hasDate = lowered.includes('date');
    const looksLong = hasCount && hasName && hasDate;

    if (looksLong) {
      const dateI = lowered.indexOf('date');
      const kindI = lowered.indexOf('kind');
      const nameI = lowered.findIndex(h => h === 'name' || h === 'metric');
      const countI = lowered.includes('count') ? lowered.indexOf('count') : lowered.indexOf('value');
      const brandI = lowered.findIndex(h => h === 'brand' || h === 'brand_name');
      const rows = lines.slice(1).map(splitter).map(cols => ({
        date: cols[dateI],
        brand: brandI >= 0 ? cols[brandI] : undefined,
        kind: kindI >= 0 ? cols[kindI] : undefined,
        name: cols[nameI],
        count: cols[countI],
      }));
      const body = { format: 'long', rows };
      if (brand?.brand_id) body.brand_id = brand.brand_id;  // fallback for rows without a brand cell
      return body;
    }

    // Matrix layout. Detect whether column 0 is "Brand".
    const brandInCol0 = lowered[0] === 'brand';
    const dateStart = brandInCol0 ? 2 : 1;  // if Brand col present, dates start at column 2
    const dates = first.slice(dateStart).map(s => s.trim()).filter(Boolean);
    if (!dates.length) return null;
    const rows = lines.slice(1).map(splitter).map(cols => {
      const brandCell = brandInCol0 ? (cols[0] || '').trim() : '';
      const name = ((brandInCol0 ? cols[1] : cols[0]) || '').trim();
      const rawValues = cols.slice(dateStart, dateStart + dates.length);
      return {
        brand: brandCell,
        name,
        values: rawValues.map(v => {
          const t = (v || '').trim();
          if (t === '') return null;
          const n = Number(t);
          return Number.isFinite(n) ? n : null;
        }),
      };
    }).filter(r => r.name);
    if (!rows.length) return null;
    const body = { format: 'wide', dates, rows };
    if (brand?.brand_id) body.brand_id = brand.brand_id;
    return body;
  };

  const run = async () => {
    const parsed = parsePasted();
    if (!parsed) { toast.error('Could not parse. Copy directly from Sheets/Excel (first row = dates, first column = metric names).'); return; }
    setImporting(true);
    try {
      const r = await fetch(`${API_URL}/api/competitor-tracker/entries/bulk`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(parsed),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      const brandsMsg = data.brands_touched?.length
        ? ` across ${data.brands_touched.length} brand${data.brands_touched.length === 1 ? '' : 's'}`
        : '';
      const createdMsg = data.created_brands?.length
        ? ` (auto-created: ${data.created_brands.join(', ')})`
        : '';
      toast.success(`Imported ${data.imported} entries${brandsMsg}${createdMsg}`);
      if (data.errors?.length) toast.warning(`${data.errors.length} rows had issues (see console)`, {
        description: data.errors.slice(0, 3).join('; ')
      });
      onImported(); onClose();
    } catch (e) { toast.error(`Import failed: ${e.message}`); } finally { setImporting(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-3xl">
        <DialogHeader><DialogTitle>Bulk import · {brand.brand_name}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-sm text-slate-400">
            Upload a CSV file, paste from Google Sheets/Excel, or click <span className="text-cyan-300">Fill with sample</span> to see the shape.
            <br />
            <span className="text-slate-500">Add a <span className="text-cyan-300">Brand</span> column to import many brands at once — new brand names get auto-created.</span>
          </p>
          <div className="flex flex-wrap gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values,text/plain"
              className="hidden"
              onChange={onPickFile}
              data-testid="tracker-bulk-file-input"
            />
            <Button
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              className="bg-cyan-500 text-slate-950 hover:bg-cyan-400 font-medium"
              data-testid="tracker-bulk-choose-file"
            >
              <Upload className="h-3.5 w-3.5 mr-1" /> Choose CSV file
            </Button>
            <Button
              size="sm" variant="outline"
              onClick={fillSample}
              className="bg-slate-800 border-slate-600 text-slate-100 hover:bg-slate-700 hover:text-white"
              data-testid="tracker-bulk-sample-btn"
            >
              <ClipboardPaste className="h-3.5 w-3.5 mr-1" /> Fill with sample
            </Button>
            <Button
              size="sm" variant="outline"
              onClick={() => downloadTemplate()}
              className="bg-slate-800 border-slate-600 text-slate-100 hover:bg-slate-700 hover:text-white"
              data-testid="tracker-bulk-download-matrix"
            >
              Download template (.csv)
            </Button>
          </div>
          <Textarea
            rows={12}
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder={`Date         06/07/2026   09/07/2026   15/07/2026\nTotal Active Ads   1300         950          800\nCreatine           550          460          410\nPreworkout                      18           11`}
            className="bg-slate-950 border-slate-700 font-mono text-xs"
            data-testid="tracker-bulk-textarea"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={run} disabled={importing || !text.trim()} data-testid="tracker-bulk-import-btn">
            {importing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ClipboardPaste className="h-4 w-4 mr-1" />}
            Import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
